import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')
    const [focused, setFocused] = useState(false)

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    const canSubmit = !!input.trim() && !disabled

    return (
      <div className="border-t border-argan/25 bg-cream px-5 py-4">
        <div
          className={cn(
            'flex items-center gap-2 bg-white border transition-colors duration-300',
            'pl-4 pr-1.5 py-1.5',
            focused ? 'border-bruno' : 'border-argan/50'
          )}
        >
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 bg-transparent text-[14px] text-bruno',
              'placeholder:text-bruno-muted placeholder:italic placeholder:font-light',
              'border-0 outline-none focus:outline-none focus:ring-0',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'py-1.5 min-w-0'
            )}
            style={{ boxShadow: 'none' }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSubmit}
            className={cn(
              'group inline-flex items-center justify-center shrink-0',
              'w-9 h-9 transition-all duration-300',
              canSubmit
                ? 'bg-bruno text-cream hover:bg-argan-deep'
                : 'bg-cream-soft text-bruno-muted cursor-not-allowed'
            )}
            aria-label={t('chatbot.send')}
            title={t('chatbot.send')}
          >
            <ArrowRight
              className={cn(
                'w-4 h-4 transition-transform duration-300',
                canSubmit && 'group-hover:translate-x-0.5'
              )}
              strokeWidth={1.5}
            />
          </button>
        </div>

        {/* Footer hint */}
        <p className="mt-2.5 text-[9px] tracking-[0.2em] uppercase text-bruno-muted/70 text-center">
          {t('chatbot.poweredBy', 'Made with care · Nashi Argan')}
        </p>
      </div>
    )
  }
)
